/**
 * L'ONGLET FACTURES — lot 106, refondu au lot 107 (18/09/2026).
 *
 * Demandé au lot 106 après ce constat : « certaines factures je ne suis pas du
 * tout d'accord avec leur traitement », « plusieurs chiffres (BFR…) me
 * semblent démesurés ». Repris au lot 107 sur trois reproches précis : « ça
 * fait mal aux yeux », « ça ne s'édite pas », « au clic une fenêtre doit
 * s'ouvrir permettant d'éditer et de voir partout où elle est prise en compte
 * et comment ».
 *
 * CE QUI S'ÉDITE, ET RIEN D'AUTRE : la DATE DE PAIEMENT PRÉVUE. Elle
 * appartient à Stafa — c'est une prévision, pas un fait comptable. Les
 * montants, l'échéance et le statut viennent de Pennylane et ne se touchent
 * pas ici : Stafa ferait alors foi contre la comptabilité, et les deux
 * divergeraient sans que rien ne le rattrape.
 *
 * AUCUNE ROUTE NOUVELLE POUR CETTE ÉDITION : c'est celle de l'échéancier
 * (`poserPaiements`), qui écrit dans `echeancier_paiements` et journalise.
 * Une seconde route aurait fait deux vérités pour une même donnée.
 *
 * DEUX TOTAUX, JAMAIS LEUR SOMME — arbitrage du 18/09. Le tableau mélange
 * clients et fournisseurs à la demande, mais additionner une créance et une
 * dette donnerait un nombre qui ne veut rien dire.
 *
 * LE CONTENEUR APPELLE, LA VUE DESSINE — même coupure que les autres écrans,
 * pour que la vue entre dans l'aperçu.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ajouterJours,
  centimes,
  partsDe,
  prisesEnCompte,
  type TotauxFactures,
} from '@stafa/core';
import type { EchecApi } from './api.js';
import {
  ecarterFacture,
  leverExclusion,
  lireDetailPiece,
  lireFactures,
  poserCession,
  poserPaiements,
  type DetailPiece,
} from './apiAnalyse.js';
import {
  aujourdhuiLocal,
  resteADecouper,
  type PaiementPrevu,
} from './echeancier.js';
import {
  dateFr,
  LIBELLES_TRAITEMENT,
  euros,
  exportCsv,
  filtrerFactures,
  lignesDe,
  LIBELLES_STATUT,
  nomDuFichier,
  pageDe,
  partsPourLEcheancier,
  SANS_FILTRE_FACTURES,
  statutDe,
  TAILLES_PAGE,
  totauxFiltres,
  traitementDe,
  trierFactures,
  type CleTriFactures,
  type FacturesLues,
  type FiltresFactures,
  type Ligne,
  type Sens2,
} from './factures.js';

export function EcranFactures({
  jeton,
  quandAccesPerdu,
  messageRefus,
  sources,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly messageRefus: (cause: EchecApi) => string;
  /**
   * LE PANNEAU DES SOURCES PENNYLANE, rendu par l'hôte — lot 115, 22/09/2026.
   *
   * IL ÉTAIT ACCROCHÉ À L'ÉCHÉANCIER, qui va disparaître. C'est ce panneau qui
   * a permis, le 21/09, de relire les factures fournisseurs et de faire entrer
   * 286 507 € de dettes dans le BFR : le perdre avec l'écran aurait retiré le
   * seul moyen de rafraîchir la copie Pennylane.
   *
   * Sa place est ici : on constate qu'une relecture s'impose EN REGARDANT LES
   * FACTURES, pas ailleurs.
   */
  readonly sources: (quandLu: () => void) => ReactNode;
}) {
  const [donnees, setDonnees] = useState<FacturesLues | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [filtres, setFiltres] = useState<FiltresFactures>(SANS_FILTRE_FACTURES);
  const [tri, setTri] = useState<{ cle: CleTriFactures; sens: Sens2 }>({
    cle: 'echeance',
    sens: 'bas',
  });
  const [taille, setTaille] = useState(50);
  const [page, setPage] = useState(1);
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [travail, setTravail] = useState(false);

  async function jetonOuSortie(): Promise<string | null> {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }

  async function charger() {
    const j = await jetonOuSortie();
    if (j === null) return;
    const r = await lireFactures(j);
    if (r.etat === 'ok') {
      setDonnees(r.valeur);
      setRefus(null);
    } else {
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus(messageRefus(r.cause));
    }
  }

  useEffect(() => {
    void charger();
  }, [jeton]);

  // JAMAIS LA CHAÎNE VIDE — leçon du lot 105 : une date illisible passée à un
  // calcul lève pendant le rendu et vide l'application entière.
  const aujourdhui = donnees?.aujourdhui ?? aujourdhuiLocal();
  const toutes = useMemo(() => (donnees === null ? [] : lignesDe(donnees)), [donnees]);
  const filtrees = useMemo(
    () => filtrerFactures(toutes, filtres, aujourdhui),
    [toutes, filtres, aujourdhui],
  );
  const triees = useMemo(
    () => trierFactures(filtrees, tri.cle, tri.sens),
    [filtrees, tri],
  );
  const affichees = useMemo(() => pageDe(triees, taille, page), [triees, taille, page]);
  const totaux = useMemo(
    () => totauxFiltres(filtrees, aujourdhui),
    [filtrees, aujourdhui],
  );

  async function agir(
    travailler: (j: string) => Promise<{ etat: string; cause?: EchecApi }>,
  ) {
    const j = await jetonOuSortie();
    if (j === null) return;
    setTravail(true);
    const r = await travailler(j);
    setTravail(false);
    if (r.etat === 'ok') {
      await charger();
      return;
    }
    if (r.cause === 'acces') quandAccesPerdu();
    if (r.cause !== undefined) setRefus(messageRefus(r.cause));
  }

  /** L'export porte sur le FILTRE COURANT, toutes pages confondues. */
  function exporter() {
    const contenu = exportCsv(triees, aujourdhui);
    const lien = document.createElement('a');
    const blob = new Blob([contenu], { type: 'text/csv;charset=utf-8' });
    lien.href = URL.createObjectURL(blob);
    lien.download = nomDuFichier(aujourdhui);
    lien.click();
    URL.revokeObjectURL(lien.href);
  }

  return (
    <VueFactures
      sources={sources(() => void charger())}
      chargement={donnees === null}
      aujourdhui={aujourdhui}
      lignes={affichees.lignes}
      totaux={totaux}
      pages={affichees.pages}
      page={affichees.page}
      taille={taille}
      filtres={filtres}
      tri={tri}
      refus={refus}
      travail={travail}
      ouverte={triees.find((l) => `${l.sens}:${l.pennylane_id}` === ouverte) ?? null}
      quandFiltre={(f) => {
        setFiltres(f);
        setPage(1);
      }}
      quandTri={(cle) =>
        setTri((t) =>
          t.cle === cle
            ? { cle, sens: t.sens === 'haut' ? 'bas' : 'haut' }
            : { cle, sens: 'haut' },
        )
      }
      quandTaille={(n) => {
        setTaille(n);
        setPage(1);
      }}
      quandPage={setPage}
      quandOuvrir={setOuverte}
      quandEcarter={(l, motif) =>
        void agir((j) => ecarterFacture(j, l.sens, l.pennylane_id, motif))
      }
      quandLever={(l) => void agir((j) => leverExclusion(j, l.sens, l.pennylane_id))}
      quandPaiements={(l, paiements, commentaire) =>
        void agir((j) =>
          poserPaiements(
            j,
            l.sens,
            l.pennylane_id,
            paiements,
            commentaire ??
              (paiements.length === 0
                ? 'retour au calcul automatique'
                : paiements.length === 1
                  ? 'date fixée à la main'
                  : `découpé en ${String(paiements.length)} paiements`),
          ),
        )
      }
      quandCession={(l, cedeeLe) =>
        void agir((j) => poserCession(j, l.pennylane_id, cedeeLe))
      }
      quandDetail={async (l) => {
        // L'historique n'est pas un chiffre : s'il ne se lit pas, l'écran
        // continue sans lui plutôt que de refuser d'ouvrir la fenêtre.
        const j = await jetonOuSortie();
        if (j === null) return null;
        const r = await lireDetailPiece(j, l.sens, l.pennylane_id);
        return r.etat === 'ok' ? r.valeur : null;
      }}
      quandExporter={exporter}
    />
  );
}

/** Une ligne d'échéance en cours de saisie. Texte, pas nombre : on répare
 *  une virgule française au moment d'envoyer, pas à chaque frappe. */
interface LigneSaisie {
  readonly montant: string;
  readonly date: string;
}

const COLONNES: readonly {
  readonly cle: CleTriFactures;
  readonly nom: string;
  readonly nombre?: boolean;
}[] = [
  { cle: 'tiers', nom: 'Tiers' },
  { cle: 'numero', nom: 'N° pièce' },
  { cle: 'date_piece', nom: 'Date' },
  { cle: 'echeance', nom: 'Échéance' },
  { cle: 'ht', nom: 'HT', nombre: true },
  { cle: 'tva', nom: 'TVA', nombre: true },
  { cle: 'ttc', nom: 'TTC', nombre: true },
  { cle: 'reste', nom: 'Reste dû', nombre: true },
  { cle: 'statut', nom: 'Statut' },
  { cle: 'prevue', nom: 'Paiement prévu' },
  { cle: 'reglee', nom: 'Payée le' },
  { cle: 'traitement', nom: 'Traitement' },
];

const NOMBRE = new Intl.NumberFormat('fr-FR');

/** Une pièce échue et non payée se voit en rouge : c'est ce qui presse. */
function etatDe(l: Ligne, aujourdhui: string): { classe: string; mot: string } {
  const s = statutDe(l);
  if (s !== 'payee' && l.echeance !== null && l.echeance < aujourdhui) {
    return { classe: 'etat-echue', mot: 'échue' };
  }
  return { classe: `etat-${s}`, mot: LIBELLES_STATUT[s] };
}

export function VueFactures({
  chargement,
  aujourdhui,
  lignes,
  totaux,
  pages,
  page,
  taille,
  filtres,
  tri,
  refus,
  travail,
  ouverte,
  quandFiltre,
  quandTri,
  quandTaille,
  quandPage,
  quandOuvrir,
  quandEcarter,
  quandLever,
  quandPaiements,
  quandCession,
  quandDetail,
  quandExporter,
  sources,
}: {
  readonly chargement: boolean;
  readonly aujourdhui: string;
  readonly lignes: readonly Ligne[];
  readonly totaux: TotauxFactures;
  readonly pages: number;
  readonly page: number;
  readonly taille: number;
  readonly filtres: FiltresFactures;
  readonly tri: { readonly cle: CleTriFactures; readonly sens: Sens2 };
  readonly refus: string | null;
  readonly travail: boolean;
  readonly ouverte: Ligne | null;
  readonly quandFiltre: (f: FiltresFactures) => void;
  readonly quandTri: (cle: CleTriFactures) => void;
  readonly quandTaille: (n: number) => void;
  readonly quandPage: (n: number) => void;
  readonly quandOuvrir: (cle: string | null) => void;
  readonly quandEcarter: (l: Ligne, motif: string) => void;
  readonly quandLever: (l: Ligne) => void;
  readonly quandPaiements: (
    l: Ligne,
    paiements: readonly PaiementPrevu[],
    commentaire: string | null,
  ) => void;
  readonly quandCession: (l: Ligne, cedeeLe: string | null) => void;
  readonly quandDetail: (l: Ligne) => Promise<DetailPiece | null>;
  readonly quandExporter: () => void;
  readonly sources: ReactNode;
}): ReactNode {
  const [plus, setPlus] = useState(false);
  const [voirSources, setVoirSources] = useState(false);
  const poser = (bout: Partial<FiltresFactures>) =>
    quandFiltre({ ...filtres, ...bout });
  const part = (a: number, b: number) => (b <= 0 ? 0 : Math.min(100, (a / b) * 100));

  /** Une puce qui bascule un filtre : un clic au lieu de trois. */
  const puce = (nom: string, actif: boolean, bascule: () => void) => (
    <button
      type="button"
      className="fact-puce"
      aria-pressed={actif}
      onClick={bascule}
      key={nom}
    >
      {nom}
    </button>
  );

  return (
    <main className="corps corps-factures">
      <header className="fact-entete">
        <div>
          <h2>Factures</h2>
          <p className="fact-aide">
            Toutes les pièces lues chez Pennylane, seize mois en arrière. Cliquez une
            ligne pour voir où elle est prise en compte, et fixer sa date de paiement.
          </p>
        </div>
        <div className="fact-entete-boutons">
          <button
            type="button"
            className="bouton bouton-discret"
            aria-expanded={voirSources}
            onClick={() => setVoirSources((v) => !v)}
          >
            Données Pennylane
          </button>
          <button
            type="button"
            className="bouton bouton-discret"
            onClick={quandExporter}
            disabled={chargement || totaux.lignes === 0}
          >
            Exporter ({NOMBRE.format(totaux.lignes)})
          </button>
        </div>
      </header>

      {/* Replié par défaut : on relit rarement, on regarde souvent. */}
      {voirSources && <div className="fact-sources">{sources}</div>}

      {refus !== null && <p className="bandeau bandeau-refus">{refus}</p>}

      <div className="fact-totaux">
        <div className="fact-total">
          <span className="fact-total-nom">Créances clients</span>
          <span className="fact-total-valeur">{euros(totaux.creances)}</span>
          <div className="fact-jauge">
            <span
              style={{
                width: `${String(part(totaux.creances_comptees, totaux.creances))}%`,
              }}
            />
          </div>
          <span className="fact-total-detail">
            {euros(totaux.creances_comptees)} comptés au BFR
          </span>
        </div>
        <div className="fact-total">
          <span className="fact-total-nom">Dettes fournisseurs</span>
          <span className="fact-total-valeur">{euros(totaux.dettes)}</span>
          <div className="fact-jauge">
            <span
              style={{
                width: `${String(part(totaux.dettes_comptees, totaux.dettes))}%`,
              }}
            />
          </div>
          <span className="fact-total-detail">
            {euros(totaux.dettes_comptees)} comptés au BFR
          </span>
        </div>
        <div
          className={
            totaux.ecartees > 0 || totaux.sans_ht > 0
              ? 'fact-total fact-total-alerte'
              : 'fact-total'
          }
        >
          <span className="fact-total-nom">Lignes</span>
          <span className="fact-total-valeur">{NOMBRE.format(totaux.lignes)}</span>
          <span className="fact-total-detail">
            {NOMBRE.format(totaux.comptees)} comptées au BFR
          </span>
          <span className="fact-total-detail">
            {totaux.ecartees > 0 && `${NOMBRE.format(totaux.ecartees)} écartée(s)`}
            {totaux.ecartees > 0 && totaux.sans_ht > 0 && ' · '}
            {totaux.sans_ht > 0 && `${NOMBRE.format(totaux.sans_ht)} sans HT lisible`}
            {totaux.ecartees === 0 && totaux.sans_ht === 0 && 'rien à signaler'}
          </span>
        </div>
      </div>

      <div className="fact-barre">
        <input
          type="search"
          className="champ"
          placeholder="N° de pièce, tiers, libellé…"
          value={filtres.texte}
          onChange={(e) => poser({ texte: e.target.value })}
          aria-label="Chercher une facture"
        />
        <div className="fact-puces">
          {puce('Clients', filtres.sens === 'client', () =>
            poser({ sens: filtres.sens === 'client' ? 'tout' : 'client' }),
          )}
          {puce('Fournisseurs', filtres.sens === 'fournisseur', () =>
            poser({ sens: filtres.sens === 'fournisseur' ? 'tout' : 'fournisseur' }),
          )}
          {puce('Non soldées', filtres.statut === 'ouverte', () =>
            poser({ statut: filtres.statut === 'ouverte' ? 'tout' : 'ouverte' }),
          )}
          {puce('Échues', filtres.echues, () => poser({ echues: !filtres.echues }))}
          {puce('Écartées', filtres.compte === 'ecartee', () =>
            poser({ compte: filtres.compte === 'ecartee' ? 'tout' : 'ecartee' }),
          )}
          {puce('Non comptées', filtres.compte === 'non_comptee', () =>
            poser({
              compte: filtres.compte === 'non_comptee' ? 'tout' : 'non_comptee',
            }),
          )}
          {puce('Traitées à la main', filtres.traitement === 'main', () =>
            poser({ traitement: filtres.traitement === 'main' ? 'tout' : 'main' }),
          )}
          {puce('Automatiques', filtres.traitement === 'auto', () =>
            poser({ traitement: filtres.traitement === 'auto' ? 'tout' : 'auto' }),
          )}
          {puce('À dater', filtres.traitement === 'a_dater', () =>
            poser({
              traitement: filtres.traitement === 'a_dater' ? 'tout' : 'a_dater',
            }),
          )}
        </div>
        <button
          type="button"
          className="bouton bouton-discret bouton-petit"
          onClick={() => setPlus((x) => !x)}
          aria-expanded={plus}
        >
          {plus ? 'Moins de filtres' : 'Plus de filtres'}
        </button>
        <button
          type="button"
          className="bouton bouton-discret bouton-petit"
          onClick={() => quandFiltre(SANS_FILTRE_FACTURES)}
        >
          Tout effacer
        </button>
      </div>

      {plus && (
        <div className="fact-plus">
          <label>
            Statut
            <select
              className="champ"
              value={filtres.statut}
              onChange={(e) =>
                poser({ statut: e.target.value as FiltresFactures['statut'] })
              }
            >
              <option value="tout">Tous</option>
              <option value="ouverte">Ouvertes</option>
              <option value="partielle">Partiellement réglées</option>
              <option value="payee">Payées</option>
            </select>
          </label>
          <label>
            Comptée au BFR
            <select
              className="champ"
              value={filtres.compte}
              onChange={(e) =>
                poser({ compte: e.target.value as FiltresFactures['compte'] })
              }
            >
              <option value="tout">Comptées ou non</option>
              <option value="comptee">Comptées</option>
              <option value="non_comptee">Non comptées</option>
              <option value="ecartee">Écartées à la main</option>
            </select>
          </label>
          <label>
            Traitement
            <select
              className="champ"
              value={filtres.traitement}
              onChange={(ev) =>
                poser({ traitement: ev.target.value as FiltresFactures['traitement'] })
              }
            >
              <option value="tout">Tous</option>
              <option value="main">Traitées à la main</option>
              <option value="auto">Laissées au calcul</option>
              <option value="a_dater">À dater (échues, sans date prévue)</option>
            </select>
          </label>
          <label>
            Filtrer sur
            <select
              className="champ"
              value={filtres.sur}
              onChange={(e) => poser({ sur: e.target.value as FiltresFactures['sur'] })}
            >
              <option value="piece">Date de pièce</option>
              <option value="echeance">Échéance</option>
            </select>
          </label>
          <label>
            Du
            <input
              type="date"
              className="champ champ-court"
              value={filtres.du}
              onChange={(e) => poser({ du: e.target.value })}
            />
          </label>
          <label>
            Au
            <input
              type="date"
              className="champ champ-court"
              value={filtres.au}
              onChange={(e) => poser({ au: e.target.value })}
            />
          </label>
          <label>
            TTC minimum
            <input
              type="text"
              inputMode="decimal"
              className="champ champ-court"
              placeholder="€"
              value={filtres.minimum}
              onChange={(e) => poser({ minimum: e.target.value })}
            />
          </label>
        </div>
      )}

      {chargement ? (
        <p className="attente">Lecture des factures…</p>
      ) : lignes.length === 0 ? (
        <p className="sources-vide">Aucune facture ne correspond à ces filtres.</p>
      ) : (
        <div className="fact-defile">
          <table className="fact-table">
            <thead>
              <tr>
                {COLONNES.map((c) => (
                  <th key={c.cle} className={c.nombre === true ? 'nombre' : ''}>
                    <button
                      type="button"
                      className="fact-tri"
                      onClick={() => quandTri(c.cle)}
                      aria-label={`Trier par ${c.nom}`}
                    >
                      {c.nom}
                      {tri.cle === c.cle && (tri.sens === 'haut' ? ' ▲' : ' ▼')}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lignes.map((l) => {
                const cle = `${l.sens}:${l.pennylane_id}`;
                const etat = etatDe(l, aujourdhui);
                return (
                  <tr
                    key={cle}
                    className={l.ecartee !== null ? 'fact-ecartee' : ''}
                    onClick={() => quandOuvrir(cle)}
                  >
                    <td>
                      <span className="fact-tiers">{l.tiers_nom}</span>
                      <span className="fact-second">
                        {l.sens === 'client' ? 'client' : 'fournisseur'}
                      </span>
                    </td>
                    <td>{l.numero ?? <span className="fact-vide">—</span>}</td>
                    <td>{dateFr(l.date_piece)}</td>
                    <td>{dateFr(l.echeance)}</td>
                    <td className="nombre">
                      {l.ht === null ? (
                        <span className="fact-vide">—</span>
                      ) : (
                        euros(l.ht)
                      )}
                    </td>
                    <td className="nombre">
                      {l.tva === null ? (
                        <span className="fact-vide">—</span>
                      ) : (
                        euros(l.tva)
                      )}
                    </td>
                    <td className="nombre fact-ttc">{euros(l.ttc)}</td>
                    <td className="nombre">{euros(l.reste)}</td>
                    <td>
                      <span className={`fact-etat ${etat.classe}`}>{etat.mot}</span>
                    </td>
                    <td>{dateFr(l.prevue_le)}</td>
                    <td>{dateFr(l.regle_le)}</td>
                    <td>
                      <span className={`fact-traitement t-${traitementDe(l)}`}>
                        {LIBELLES_TRAITEMENT[traitementDe(l)]}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="fact-pages">
        <label>
          Par page
          <select
            className="champ"
            value={taille}
            onChange={(e) => quandTaille(Number(e.target.value))}
          >
            {TAILLES_PAGE.map((n) => (
              <option key={n} value={n}>
                {n === 0 ? 'tout' : n}
              </option>
            ))}
          </select>
        </label>
        {pages > 1 && (
          <>
            <button
              type="button"
              className="bouton bouton-discret bouton-petit"
              disabled={page <= 1}
              onClick={() => quandPage(page - 1)}
            >
              Précédente
            </button>
            <span>
              page {page} sur {pages}
            </span>
            <button
              type="button"
              className="bouton bouton-discret bouton-petit"
              disabled={page >= pages}
              onClick={() => quandPage(page + 1)}
            >
              Suivante
            </button>
          </>
        )}
      </div>

      {ouverte !== null && (
        <FenetreFacture
          ligne={ouverte}
          aujourdhui={aujourdhui}
          travail={travail}
          quandFermer={() => quandOuvrir(null)}
          quandEcarter={quandEcarter}
          quandLever={quandLever}
          quandPaiements={quandPaiements}
          quandCession={quandCession}
          quandDetail={quandDetail}
        />
      )}
    </main>
  );
}

/**
 * LA FENÊTRE : la pièce, où elle est prise en compte, ce qu'on peut y changer.
 *
 * Elle s'ouvre PAR-DESSUS et non en bas de page : un détail qu'il faut aller
 * chercher en faisant défiler n'est pas un détail, c'est une note de bas de
 * page.
 */
function FenetreFacture({
  ligne,
  aujourdhui,
  travail,
  quandFermer,
  quandEcarter,
  quandLever,
  quandPaiements,
  quandCession,
  quandDetail,
}: {
  readonly ligne: Ligne;
  readonly aujourdhui: string;
  readonly travail: boolean;
  readonly quandFermer: () => void;
  readonly quandEcarter: (l: Ligne, motif: string) => void;
  readonly quandLever: (l: Ligne) => void;
  readonly quandPaiements: (
    l: Ligne,
    paiements: readonly PaiementPrevu[],
    commentaire: string | null,
  ) => void;
  readonly quandCession: (l: Ligne, cedeeLe: string | null) => void;
  readonly quandDetail: (l: Ligne) => Promise<DetailPiece | null>;
}): ReactNode {
  const [motif, setMotif] = useState('');
  const [commentaire, setCommentaire] = useState('');

  /**
   * LES ÉCHÉANCES SAISIES — lot 114, 22/09/2026.
   *
   * Une facture peut être réglée en PLUSIEURS FOIS, et le calcul le sait
   * depuis le lot 97 (`partsDe`, packages/core/src/echeancier.ts). Seule cette
   * fenêtre ne l'offrait pas : elle ne posait qu'une date. On portait donc la
   * saisie multiple dans l'Échéancier, et la même pièce s'éditait à deux
   * endroits qui ne faisaient pas la même chose.
   *
   * MONTANT VIDE SUR UNE SEULE LIGNE = TOUT LE RESTE DÛ à cette date. C'est
   * la convention de l'API (`poserPaiements`), pas une invention d'écran.
   */
  const [lignes, setLignes] = useState<readonly LigneSaisie[]>([]);

  /**
   * L'HISTORIQUE DES DATES — lot 114, 22/09/2026.
   *
   * Il est lu PAR UNE AUTRE ROUTE (`GET /analyse/echeancier/:cote/:id`) que la
   * liste des factures : le charger pour les 1 934 lignes d'un coup serait
   * absurde, alors qu'on n'en regarde qu'une à la fois. Il arrive donc APRÈS
   * l'ouverture de la fenêtre, et l'écran dit qu'il charge plutôt que de
   * laisser un blanc.
   *
   * `null` : pas encore lu, ou illisible. La distinction ne change rien à
   * l'affichage — dans les deux cas il n'y a rien à montrer.
   */
  const [detail, setDetail] = useState<DetailPiece | null>(null);

  useEffect(() => {
    let vivant = true;
    setDetail(null);
    void quandDetail(ligne).then((d) => {
      if (vivant) setDetail(d);
    });
    return () => {
      vivant = false;
    };
  }, [ligne.pennylane_id, ligne.sens, quandDetail, ligne]);

  useEffect(() => {
    setLignes(
      ligne.paiements.length === 0
        ? [{ montant: '', date: ligne.prevue_le ?? '' }]
        : ligne.paiements.map((x) => ({ montant: x.montant ?? '', date: x.date })),
    );
    setCommentaire('');
    setMotif('');
  }, [ligne.pennylane_id, ligne.paiements, ligne.prevue_le]);

  const plusieurs = lignes.length >= 2;
  const ecart = plusieurs
    ? resteADecouper(
        lignes.map((l) => l.montant),
        (ligne.reste / 100).toFixed(2),
      )
    : 0;
  const complet =
    lignes.every((l) => l.date !== '') &&
    (!plusieurs || lignes.every((l) => centimes(l.montant.replace(',', '.')) > 0));
  const motifCourt = motif.trim().length < 3;
  const etat = etatDe(ligne, aujourdhui);

  const parts = partsPourLEcheancier(ligne, aujourdhui);
  const pris = prisesEnCompte(ligne, parts, aujourdhui);

  /**
   * ON N'ÉDITE QUE CE QUI EST ÉDITABLE, et on dit pourquoi sinon. La route de
   * l'échéancier ne connaît que les pièces OUVERTES et NON ÉCARTÉES (vérifié
   * dans `analyse_echeancier.ts`, `piecesOuvertes`) : offrir un champ qui
   * échouerait serait pire que ne rien offrir.
   */
  const editable = ligne.ecartee === null && !ligne.payee && ligne.reste > 0;

  return (
    <div
      className="fact-voile"
      role="dialog"
      aria-modal="true"
      aria-label="Détail de la facture"
      onClick={quandFermer}
    >
      <div className="fact-fenetre" onClick={(e) => e.stopPropagation()}>
        <div className="fact-fenetre-entete">
          <div>
            <h3>{ligne.numero ?? `Pièce n° ${ligne.pennylane_id}`}</h3>
            <p className="fact-aide">
              {ligne.tiers_nom} · {ligne.sens === 'client' ? 'client' : 'fournisseur'}{' '}
              <span className={`fact-etat ${etat.classe}`}>{etat.mot}</span>
            </p>
          </div>
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            onClick={quandFermer}
          >
            Fermer
          </button>
        </div>

        <section>
          <h4>La pièce, telle que Pennylane la donne</h4>
          <dl className="fact-detail">
            <div>
              <dt>Date de pièce</dt>
              <dd>{dateFr(ligne.date_piece)}</dd>
            </div>
            <div>
              <dt>Échéance</dt>
              <dd>{dateFr(ligne.echeance)}</dd>
            </div>
            <div>
              <dt>HT</dt>
              <dd className="chiffre">
                {ligne.ht === null ? 'non lisible' : euros(ligne.ht, true)}
              </dd>
            </div>
            <div>
              <dt>TVA</dt>
              <dd className="chiffre">
                {ligne.tva === null ? 'non lisible' : euros(ligne.tva, true)}
              </dd>
            </div>
            <div>
              <dt>TTC</dt>
              <dd className="chiffre">{euros(ligne.ttc, true)}</dd>
            </div>
            <div>
              <dt>Reste dû</dt>
              <dd className="chiffre">{euros(ligne.reste, true)}</dd>
            </div>
            <div>
              <dt>Statut chez Pennylane</dt>
              <dd>{ligne.statut ?? '—'}</dd>
            </div>
            <div>
              <dt>Paiement constaté</dt>
              <dd>
                {ligne.regle_le !== null
                  ? dateFr(ligne.regle_le)
                  : ligne.regle_cherche
                    ? 'cherché, rien de rapproché'
                    : 'pas encore cherché'}
              </dd>
            </div>
            {ligne.cedee_le !== null && (
              <div>
                <dt>Cédée au factor</dt>
                <dd>{dateFr(ligne.cedee_le)}</dd>
              </div>
            )}
          </dl>
        </section>

        <section>
          <h4>Où cette facture est prise en compte</h4>
          <div className="fact-pris">
            {pris.map((p) => (
              <div
                key={p.ecran}
                className={
                  p.compte ? 'fact-pris-ligne fact-pris-oui' : 'fact-pris-ligne'
                }
              >
                <span className="fact-pris-nom">{p.nom}</span>
                <span className="fact-pris-comment">
                  {p.compte ? p.comment : `non comptée — ${p.comment}`}
                </span>
                <span className="fact-pris-montant">
                  {p.montant === null ? '' : euros(p.montant)}
                  {p.quand !== null && p.montant !== null && ` · ${dateFr(p.quand)}`}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h4>Date de paiement prévue</h4>
          {editable ? (
            <>
              {ligne.saisie !== null && (
                <p className="fact-aide">
                  <span className="fact-traitement t-date_fixee">à la main</span> fixée
                  par {ligne.saisie.par}, le {dateFr(ligne.saisie.le.slice(0, 10))}.
                </p>
              )}
              <p className="fact-aide">
                C’est une prévision de Stafa, pas un fait comptable : la changer déplace
                la pièce dans l’échéancier, la trésorerie et les encaissements attendus.
                Les montants et l’échéance de Pennylane ne bougent pas.
              </p>
              {lignes.map((l, i) => (
                <div className="fact-paiement" key={i}>
                  <input
                    type="date"
                    className="champ"
                    value={l.date}
                    aria-label={`Date du paiement ${String(i + 1)}`}
                    onChange={(e) =>
                      setLignes(
                        lignes.map((x, k) =>
                          k === i ? { ...x, date: e.target.value } : x,
                        ),
                      )
                    }
                  />
                  {plusieurs && (
                    <input
                      type="text"
                      className="champ fact-montant"
                      inputMode="decimal"
                      value={l.montant}
                      placeholder="montant"
                      aria-label={`Montant du paiement ${String(i + 1)}`}
                      onChange={(e) =>
                        setLignes(
                          lignes.map((x, k) =>
                            k === i ? { ...x, montant: e.target.value } : x,
                          ),
                        )
                      }
                    />
                  )}
                  {plusieurs && (
                    <button
                      type="button"
                      className="bouton bouton-discret"
                      onClick={() => setLignes(lignes.filter((_, k) => k !== i))}
                    >
                      Retirer
                    </button>
                  )}
                </div>
              ))}

              <div className="fact-paiement">
                <button
                  type="button"
                  className="bouton bouton-discret"
                  disabled={travail || lignes.length >= 24}
                  onClick={() =>
                    setLignes([
                      ...(lignes.length === 1
                        ? [{ ...(lignes[0] as LigneSaisie), montant: '' }]
                        : lignes),
                      { montant: '', date: '' },
                    ])
                  }
                >
                  + Ajouter une échéance
                </button>
                {plusieurs && (
                  <span className={ecart === 0 ? 'fact-ok' : 'fact-ko'}>
                    {ecart === 0
                      ? 'Le total fait le reste dû.'
                      : ecart > 0
                        ? `Il manque ${euros(ecart, true)}.`
                        : `${euros(-ecart, true)} de trop.`}
                  </span>
                )}
              </div>

              <label className="fact-commentaire">
                Commentaire (facultatif)
                <input
                  type="text"
                  className="champ"
                  maxLength={500}
                  value={commentaire}
                  placeholder="accord téléphonique, relance…"
                  onChange={(e) => setCommentaire(e.target.value)}
                />
              </label>

              <div className="fact-paiement">
                <button
                  type="button"
                  className="bouton"
                  disabled={travail || !complet || ecart !== 0}
                  onClick={() =>
                    quandPaiements(
                      ligne,
                      plusieurs
                        ? lignes.map((l) => ({
                            montant: l.montant.replace(',', '.'),
                            date: l.date,
                          }))
                        : [{ montant: null, date: lignes[0]?.date ?? '' }],
                      commentaire.trim() === '' ? null : commentaire.trim(),
                    )
                  }
                >
                  {travail ? 'En cours…' : 'Enregistrer'}
                </button>
                {ligne.paiements.length > 0 && (
                  <button
                    type="button"
                    className="bouton bouton-discret"
                    disabled={travail}
                    onClick={() => quandPaiements(ligne, [], 'retour au calcul automatique')}
                  >
                    Revenir au calcul automatique
                  </button>
                )}
              </div>
            </>
          ) : (
            <p className="fact-aide">
              {ligne.ecartee !== null
                ? 'Cette facture est écartée des calculs : remettez-la d’abord dans les calculs pour lui donner une date.'
                : 'Une pièce soldée n’a pas de date prévue : elle a une date de paiement constatée.'}
            </p>
          )}
        </section>

        {/*
          L'HISTORIQUE — lot 114, 22/09/2026.

          ON Y ÉCRIT, ON N'Y CORRIGE RIEN (migration `20260918120000`). C'est
          lui qui mesurera un jour la fiabilité des prévisions ; en attendant,
          il répond à « qui a décidé ça, et pourquoi ».
        */}
        <section>
          <h4>Historique des dates prévues</h4>
          {detail === null ? (
            <p className="fact-aide">Lecture…</p>
          ) : detail.historique.length === 0 ? (
            <p className="fact-aide">
              Personne n’a encore fixé de date : la pièce suit le calcul automatique.
            </p>
          ) : (
            <ul className="fact-historique">
              {detail.historique.map((h, i) => (
                <li key={i}>
                  <span className="fact-h-quand">
                    {dateFr(h.le.slice(0, 10))} · {h.par}
                  </span>{' '}
                  {h.apres.length === 0
                    ? 'retour au calcul automatique'
                    : h.apres.length === 1
                      ? `date fixée au ${dateFr(h.apres[0]?.date ?? null)}`
                      : `découpé en ${String(h.apres.length)} paiements`}
                  {h.commentaire !== null && (
                    <span className="fact-h-note"> — {h.commentaire}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/*
          L'AFFACTURAGE — lot 114, 22/09/2026.

          CÔTÉ CLIENT SEULEMENT, et ce n'est pas un oubli : on ne cède pas une
          dette. La route `PUT /analyse/cessions/:id` ne connaît d'ailleurs que
          `pl_factures_clients` (analyse_echeancier.ts).

          Une pièce cédée entre dans le BFR brut puis se retranche du BFR net
          (bfr.ts:606-617), et la trésorerie la découpe en deux mouvements : le
          financement à la cession, la retenue au paiement du client.
        */}
        {ligne.sens === 'client' && (
          <section>
            <h4>Affacturage</h4>
            <label className="fact-bascule">
              <input
                type="checkbox"
                checked={ligne.cedee_le !== null}
                disabled={travail}
                onChange={(e) =>
                  quandCession(
                    ligne,
                    e.target.checked
                      ? (ligne.cedee_le ??
                        ajouterJours(ligne.date_piece ?? aujourdhui, 2))
                      : null,
                  )
                }
              />
              Facture cédée au factor
            </label>
            {ligne.cedee_le !== null && (
              <div className="fact-paiement">
                <label className="fact-aide" htmlFor="fact-cession">
                  Date de cession
                </label>
                <input
                  id="fact-cession"
                  type="date"
                  className="champ"
                  value={ligne.cedee_le}
                  disabled={travail}
                  onChange={(e) =>
                    e.target.value !== '' && quandCession(ligne, e.target.value)
                  }
                />
              </div>
            )}
            <p className="fact-aide">
              {ligne.cedee_le === null
                ? 'Proposée à la cocher : date de la facture + 2 jours, à corriger si la cession a eu lieu un autre jour.'
                : 'Elle compte dans le BFR brut, puis se retranche du BFR net. La trésorerie l’encaisse en deux fois : le financement à la cession, la retenue au paiement du client.'}
            </p>
          </section>
        )}

        <section className="fact-ecarter">
          {ligne.ecartee === null ? (
            <>
              <h4>Écarter des calculs</h4>
              <p className="fact-aide">
                Elle sortira du BFR, de la trésorerie, de l’échéancier et des
                indicateurs. <strong>Jamais du Résultat.</strong> Rien n’est changé chez
                Pennylane : si la facture est réellement soldée, c’est là-bas qu’il faut
                la solder.
              </p>
              <textarea
                className="champ"
                rows={2}
                placeholder="Pourquoi ? (obligatoire)"
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                aria-label="Motif de l’exclusion"
              />
              <div className="fact-actions">
                <button
                  type="button"
                  className="bouton"
                  disabled={motifCourt || travail}
                  onClick={() => quandEcarter(ligne, motif.trim())}
                >
                  {travail ? 'En cours…' : 'Écarter'}
                </button>
                {motifCourt && motif !== '' && (
                  <span className="fact-aide">Trois signes au moins.</span>
                )}
              </div>
            </>
          ) : (
            <>
              <h4>Écartée des calculs</h4>
              <p className="fact-motif-dit">« {ligne.ecartee.motif} »</p>
              <p className="fact-aide">
                par {ligne.ecartee.par}, le {dateFr(ligne.ecartee.le.slice(0, 10))}
              </p>
              <div className="fact-actions">
                <button
                  type="button"
                  className="bouton"
                  disabled={travail}
                  onClick={() => quandLever(ligne)}
                >
                  {travail ? 'En cours…' : 'Remettre dans les calculs'}
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export { partsDe };
